/**
 * React Query Hooks for Knowledge Bases API
 * 
 * Provides data fetching, mutations, and caching for knowledge bases.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import apiClient, { ApiResponse, PaginatedResponse, getErrorMessage } from '../lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

export type KnowledgeBaseScopeType = 'shared' | 'client' | 'internal' | 'restricted';

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string | null;
  scope_type: KnowledgeBaseScopeType;
  is_active: boolean;
  collection_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateKnowledgeBaseInput {
  name: string;
  description?: string;
  scope_type?: KnowledgeBaseScopeType;
}

export interface UpdateKnowledgeBaseInput {
  name?: string;
  description?: string;
  scope_type?: KnowledgeBaseScopeType;
  is_active?: boolean;
}

export interface KnowledgeBaseListParams {
  page?: number;
  page_size?: number;
  search?: string;
}

// =============================================================================
// Query Keys
// =============================================================================

export const knowledgeBaseKeys = {
  all: (tenantId: string | undefined) => ['knowledge-bases', tenantId] as const,
  lists: (tenantId: string | undefined) => [...knowledgeBaseKeys.all(tenantId), 'list'] as const,
  list: (tenantId: string | undefined, params: KnowledgeBaseListParams) => 
    [...knowledgeBaseKeys.lists(tenantId), params] as const,
  details: (tenantId: string | undefined) => [...knowledgeBaseKeys.all(tenantId), 'detail'] as const,
  detail: (tenantId: string | undefined, id: string) => [...knowledgeBaseKeys.details(tenantId), id] as const,
};

// =============================================================================
// Knowledge Base API Functions
// =============================================================================

async function fetchKnowledgeBases(params: KnowledgeBaseListParams = {}): Promise<PaginatedResponse<KnowledgeBase>> {
  const { data } = await apiClient.get<PaginatedResponse<KnowledgeBase>>('/api/v1/knowledge/knowledge-bases', { params });
  return data;
}

async function fetchKnowledgeBase(id: string): Promise<KnowledgeBase> {
  const { data } = await apiClient.get<ApiResponse<KnowledgeBase>>(`/api/v1/knowledge/knowledge-bases/${id}`);
  return data.data;
}

async function createKnowledgeBase(input: CreateKnowledgeBaseInput): Promise<KnowledgeBase> {
  const { data } = await apiClient.post<ApiResponse<KnowledgeBase>>('/api/v1/knowledge/knowledge-bases', input);
  return data.data;
}

async function updateKnowledgeBase(id: string, input: UpdateKnowledgeBaseInput): Promise<KnowledgeBase> {
  const { data } = await apiClient.patch<ApiResponse<KnowledgeBase>>(`/api/v1/knowledge/knowledge-bases/${id}`, input);
  return data.data;
}

async function deleteKnowledgeBase(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/knowledge/knowledge-bases/${id}`);
}

// =============================================================================
// Knowledge Base Query Hooks
// =============================================================================

/**
 * List knowledge bases with pagination and filtering
 * Only runs when a tenant is selected.
 */
export function useKnowledgeBases(
  params: KnowledgeBaseListParams = {},
  options?: Omit<UseQueryOptions<PaginatedResponse<KnowledgeBase>>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: knowledgeBaseKeys.list(tenantId, params),
    queryFn: () => fetchKnowledgeBases(params),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Get single knowledge base by ID
 * Only runs when a tenant is selected.
 */
export function useKnowledgeBase(
  id: string,
  options?: Omit<UseQueryOptions<KnowledgeBase>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: knowledgeBaseKeys.detail(tenantId, id),
    queryFn: () => fetchKnowledgeBase(id),
    enabled: !!id && !!tenantId,
    staleTime: 60 * 1000,
    ...options,
  });
}

// =============================================================================
// Knowledge Base Mutation Hooks
// =============================================================================

/**
 * Create a new knowledge base
 */
export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: createKnowledgeBase,
    onSuccess: (newKB) => {
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.lists(tenantId) });
      queryClient.setQueryData(knowledgeBaseKeys.detail(tenantId, newKB.id), newKB);
    },
    onError: (error) => {
      console.error('Failed to create knowledge base:', getErrorMessage(error));
    },
  });
}

/**
 * Update a knowledge base
 */
export function useUpdateKnowledgeBase() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateKnowledgeBaseInput & { id: string }) =>
      updateKnowledgeBase(id, input),
    onSuccess: (updatedKB) => {
      queryClient.setQueryData(knowledgeBaseKeys.detail(tenantId, updatedKB.id), updatedKB);
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to update knowledge base:', getErrorMessage(error));
    },
  });
}

/**
 * Delete a knowledge base
 */
export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: deleteKnowledgeBase,
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: knowledgeBaseKeys.detail(tenantId, deletedId) });
      queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to delete knowledge base:', getErrorMessage(error));
    },
  });
}
